// src/components/NotificationTemplates/NotificationTemplates.jsx
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFileAlt,
    faPlus,
    faEdit,
    faTrash,
    faEye,
    faCheck,
    faTimes,
    faCopy,
    faSave
} from '@fortawesome/free-solid-svg-icons';
import { useNotificationContext } from '../../contexts/NotificationContext';

const NotificationTemplates = () => {
    const {
        templates,
        createTemplate,
        updateTemplate,
        loading
    } = useNotificationContext();

    const [showModal, setShowModal] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        channel: 'email',
        subject: '',
        content: '',
        variables: []
    });
    const [previewData, setPreviewData] = useState({});
    const [showPreview, setShowPreview] = useState(false);

    const channels = [
        { id: 'email', name: 'Email', icon: '📧' },
        { id: 'telegram', name: 'Telegram', icon: '📱' },
        { id: 'push', name: 'Push', icon: '🔔' }
    ];

    const defaultTemplates = [
        {
            name: 'Bienvenida',
            description: 'Mensaje de bienvenida para nuevos clientes',
            channel: 'email',
            subject: '¡Bienvenido a {{marca}}!',
            content: `Hola {{nombre}},

¡Bienvenido a {{marca}}! Nos alegra tenerte como cliente.

Tu número de cliente es: {{numeroCliente}}

Gracias por confiar en nosotros.

Saludos,
El equipo de {{marca}}`,
            variables: ['nombre', 'marca', 'numeroCliente']
        },
        {
            name: 'Puntos Acumulados',
            description: 'Notificación cuando el cliente acumula puntos',
            channel: 'push',
            subject: '',
            content: `¡Felicitaciones {{nombre}}! 

Has acumulado {{puntos}} puntos en {{marca}}.

Total de puntos: {{totalPuntos}}

¡Sigue comprando para obtener más beneficios!`,
            variables: ['nombre', 'puntos', 'marca', 'totalPuntos']
        },
        {
            name: 'Canje de Premio',
            description: 'Confirmación de canje de premio',
            channel: 'email',
            subject: 'Premio canjeado - {{marca}}',
            content: `Hola {{nombre}},

Has canjeado exitosamente tu premio: {{premio}}

Puntos utilizados: {{puntosUtilizados}}
Puntos restantes: {{puntosRestantes}}

Presenta este email en tu próxima visita para reclamar tu premio.

¡Gracias por tu fidelidad!

Saludos,
El equipo de {{marca}}`,
            variables: ['nombre', 'premio', 'puntosUtilizados', 'puntosRestantes', 'marca']
        }
    ];

    const handleNewTemplate = () => {
        setEditingTemplate(null);
        setFormData({
            name: '',
            description: '',
            channel: 'email',
            subject: '',
            content: '',
            variables: []
        });
        setShowModal(true);
    };

    const handleEditTemplate = (template) => {
        setEditingTemplate(template);
        setFormData({
            name: template.name,
            description: template.description,
            channel: template.channel,
            subject: template.subject || '',
            content: template.content,
            variables: template.variables || []
        });
        setShowModal(true);
    };

    const handleDeleteTemplate = async (templateId) => {
        if (window.confirm('¿Estás seguro de que quieres eliminar este template?')) {
            try {
                // Aquí se llamaría al endpoint de eliminación
                console.log('Eliminando template:', templateId);
            } catch (error) {
                console.error('Error eliminando template:', error);
            }
        }
    };

    const handleUseDefaultTemplate = (template) => {
        setEditingTemplate(null);
        setFormData({
            name: template.name,
            description: template.description,
            channel: template.channel,
            subject: template.subject || '',
            content: template.content,
            variables: template.variables || []
        });
        setShowModal(true);
    };

    const extractVariables = (content) => {
        const variableRegex = /\{\{(\w+)\}\}/g;
        const variables = [];
        let match;
        while ((match = variableRegex.exec(content)) !== null) {
            variables.push(match[1]);
        }
        return [...new Set(variables)];
    };

    const handleContentChange = (content) => {
        const variables = extractVariables(content);
        setFormData(prev => ({
            ...prev,
            content,
            variables
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            if (editingTemplate) {
                await updateTemplate(editingTemplate._id, formData);
            } else {
                await createTemplate(formData);
            }

            setShowModal(false);
            setFormData({
                name: '',
                description: '',
                channel: 'email',
                subject: '',
                content: '',
                variables: []
            });
        } catch (error) {
            console.error('Error guardando template:', error);
        }
    };

    const getPreviewContent = () => {
        let content = formData.content;
        Object.keys(previewData).forEach(variable => {
            const value = previewData[variable] || `{{${variable}}}`;
            content = content.replace(new RegExp(`\\{\\{${variable}\\}\\}`, 'g'), value);
        });
        return content;
    };

    const getChannelIcon = (channel) => {
        const channelInfo = channels.find(c => c.id === channel);
        return channelInfo ? channelInfo.icon : '📧';
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                        <FontAwesomeIcon icon={faFileAlt} className="text-blue-500 text-2xl mr-3" />
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800">
                                Templates de Notificaciones
                            </h2>
                            <p className="text-gray-600">
                                Gestiona los templates para tus notificaciones
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleNewTemplate}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center"
                    >
                        <FontAwesomeIcon icon={faPlus} className="mr-2" />
                        Nuevo Template
                    </button>
                </div>
            </div>

            {/* Templates existentes */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Templates Personalizados
                </h3>

                {templates.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        No hay templates personalizados. Crea uno nuevo o usa uno de los predeterminados.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {templates.map((template) => (
                            <div key={template._id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center">
                                        <span className="text-2xl mr-2">{getChannelIcon(template.channel)}</span>
                                        <div>
                                            <h4 className="font-medium text-gray-800">{template.name}</h4>
                                            <p className="text-sm text-gray-600 capitalize">{template.channel}</p>
                                        </div>
                                    </div>
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => handleEditTemplate(template)}
                                            className="text-blue-600 hover:text-blue-800"
                                            title="Editar"
                                        >
                                            <FontAwesomeIcon icon={faEdit} />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteTemplate(template._id)}
                                            className="text-red-600 hover:text-red-800"
                                            title="Eliminar"
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </div>
                                </div>

                                <p className="text-sm text-gray-600 mb-3">{template.description}</p>

                                <div className="text-xs text-gray-500">
                                    Variables: {template.variables?.length || 0}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Templates predeterminados */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Templates Predeterminados
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {defaultTemplates.map((template, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center">
                                    <span className="text-2xl mr-2">{getChannelIcon(template.channel)}</span>
                                    <div>
                                        <h4 className="font-medium text-gray-800">{template.name}</h4>
                                        <p className="text-sm text-gray-600 capitalize">{template.channel}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleUseDefaultTemplate(template)}
                                    className="text-blue-600 hover:text-blue-800"
                                    title="Usar como base"
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                </button>
                            </div>

                            <p className="text-sm text-gray-600 mb-3">{template.description}</p>

                            <div className="text-xs text-gray-500">
                                Variables: {template.variables?.length || 0}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Modal de edición/creación */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-800">
                                    {editingTemplate ? 'Editar Template' : 'Nuevo Template'}
                                </h3>
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Nombre del Template
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                            placeholder="Ej: Bienvenida"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Canal
                                        </label>
                                        <select
                                            value={formData.channel}
                                            onChange={(e) => setFormData(prev => ({ ...prev, channel: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            {channels.map((channel) => (
                                                <option key={channel.id} value={channel.id}>
                                                    {channel.icon} {channel.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Descripción
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.description}
                                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                        placeholder="Descripción del template"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                {formData.channel === 'email' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Asunto
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.subject}
                                            onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                                            placeholder="Asunto del email"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Contenido
                                    </label>
                                    <textarea
                                        value={formData.content}
                                        onChange={(e) => handleContentChange(e.target.value)}
                                        placeholder="Contenido del mensaje. Usa {{variable}} para variables."
                                        rows={8}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>

                                {/* Variables detectadas */}
                                {formData.variables.length > 0 && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Variables Detectadas
                                        </label>
                                        <div className="flex flex-wrap gap-2">
                                            {formData.variables.map((variable) => (
                                                <span
                                                    key={variable}
                                                    className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md"
                                                >
                                                    {variable}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Vista previa */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Vista Previa
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => setShowPreview(!showPreview)}
                                            className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                                        >
                                            <FontAwesomeIcon icon={showPreview ? faEye : faEye} className="mr-1" />
                                            {showPreview ? 'Ocultar' : 'Mostrar'}
                                        </button>
                                    </div>

                                    {showPreview && (
                                        <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
                                            {formData.channel === 'email' && formData.subject && (
                                                <div className="mb-2">
                                                    <strong>Asunto:</strong> {formData.subject}
                                                </div>
                                            )}
                                            <div className="whitespace-pre-wrap text-gray-800">
                                                {getPreviewContent()}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Botones */}
                                <div className="flex justify-end space-x-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowModal(false)}
                                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center disabled:opacity-50"
                                    >
                                        <FontAwesomeIcon icon={editingTemplate ? faSave : faPlus} className="mr-2" />
                                        {loading ? 'Guardando...' : (editingTemplate ? 'Actualizar' : 'Crear')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationTemplates; 